import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  cardCheckoutSessionResponseSchema,
  cardOrderCheckoutRequestSchema
} from '@inktap/contracts/customer-commerce';
import { request } from '../../lib/api';
import { formatMoney } from '../../lib/money';
import { formatDate, formatStatus } from '../../pages/portalTypes';

import {
  customerCardsResponseSchema,
  customerCardNameResponseSchema,
  customerCardLifecycleResponseSchema,
  customerCardReplacementResponseSchema,
  type CustomerCardsResponse,
  type CustomerPhysicalCard,
  type PhysicalCardState
} from '@inktap/contracts/customer-cards';
import { useSession } from '../../session';

const ACTION_COPY: Partial<Record<PhysicalCardState, string>> = {
  ACTIVE: 'Reactivate',
  SUSPENDED: 'Disable temporarily',
  LOST: 'Mark lost',
  RETIRED: 'Retire card',
  DESTROYED: 'Mark destroyed'
};

const STATE_COPY: Record<PhysicalCardState, string> = {
  RESERVED: 'Reserved for checkout',
  PROVISIONING: 'Being prepared',
  ACTIVE: 'Ready to tap',
  SUSPENDED: 'Temporarily disabled',
  LOST: 'Reported lost',
  REPLACED: 'Replaced by another card',
  RETIRED: 'Permanently retired',
  DESTROYED: 'Recorded as destroyed'
};

const readinessLabel = (value: CustomerPhysicalCard['readiness']['nfc']) =>
  value === 'ready' ? 'Ready' : value === 'failed' ? 'Needs review' : 'Not tested';

const requiresPermanentConfirmation = (state: PhysicalCardState) =>
  state === 'RETIRED' || state === 'DESTROYED';

const OPEN_REPLACEMENT_STATUSES = new Set([
  'REQUESTED',
  'REVIEWING',
  'APPROVED',
  'ACTION_REQUIRED',
  'REPLACEMENT_IN_PROGRESS'
]);

function PhysicalCardControls() {
  const { session } = useSession();
  // Workspace selection performs a full navigation. These primitives additionally
  // retire this mounted view when its locally observable customer identity changes.
  const ownerKey = JSON.stringify([session?.email, session?.expiresAt, session?.slug,
    session?.publicSlug, session?.active, session?.subscriptionPlan, session?.subscriptionStatus, session?.entitlements?.canManagePhysicalCards]);
  type Review = { cards: CustomerPhysicalCard[]; scope: CustomerCardsResponse['scope'];
    capability: CustomerCardsResponse['capability']; nextCursor: string | null; cursors: Set<string> };
  type Command = { id: symbol; owner: string };
  const authority = useRef({ owner: ownerKey, mounted: false, generation: 0,
    read: null as AbortController | null, review: null as Review | null,
    scope: null as CustomerCardsResponse['scope'] | null, command: null as Command | null });
  if (authority.current.owner !== ownerKey) {
    authority.current.read?.abort();
    authority.current = { owner: ownerKey, mounted: false, generation: authority.current.generation + 1,
      read: null, review: null, scope: null, command: null };
  }
  const [viewOwner, setViewOwner] = useState(ownerKey);
  const [cards, setCards] = useState<CustomerPhysicalCard[]>([]);
  const [capability, setCapability] = useState<CustomerCardsResponse['capability'] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyCard, setBusyCard] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadCards = useCallback(async (cursor: string | null = null, command?: Command) => {
    const owner = authority.current;
    if (!owner.mounted || (owner.command && owner.command !== command)) return;
    const previous = owner.review;
    if (cursor && (!previous || previous.nextCursor !== cursor || owner.read || previous.cursors.has(cursor))) return;
    owner.read?.abort();
    const controller = new AbortController();
    const generation = ++owner.generation;
    owner.read = controller;
    owner.review = null;
    setReviewed(false);
    setNextCursor(null);
    setCapability(null);
    setError(null);
    if (cursor) setLoadingMore(true);
    else { setLoading(true); setCards([]); }
    const current = () => authority.current === owner && owner.mounted && owner.generation === generation;
    let scopeMismatch = false;
    try {
      const query = new URLSearchParams({ take: '50' });
      if (cursor) query.set('cursor', cursor);
      const raw = await request<unknown>(`/me/cards?${query.toString()}`, { signal: controller.signal });
      if (!current()) return;
      const parsed = customerCardsResponseSchema.safeParse(raw);
      if (!parsed.success) throw new Error('Invalid card directory.');
      const response = parsed.data;
      scopeMismatch = Boolean(owner.scope && (response.scope.tenantId !== owner.scope.tenantId || response.scope.workspaceId !== owner.scope.workspaceId));
      if (response.query.cursor !== cursor || response.query.take !== 50 ||
          scopeMismatch ||
          (previous && cursor && (response.capability.enabled !== previous.capability.enabled || response.capability.name !== previous.capability.name ||
            response.items.some((card) => previous.cards.some((item) => item.publicId === card.publicId)) ||
            (response.nextCursor !== null && previous.cursors.has(response.nextCursor))))) {
        throw new Error('Uncorrelated card directory.');
      }
      owner.scope = response.scope;
      const acceptedCards = cursor && previous ? [...previous.cards, ...response.items] : response.items;
      const cursors = cursor && previous ? new Set(previous.cursors) : new Set<string>();
      if (cursor) cursors.add(cursor);
      owner.review = { cards: acceptedCards, scope: response.scope, capability: response.capability,
        nextCursor: response.nextCursor, cursors };
      setCards(acceptedCards);
      setCapability(response.capability);
      setNextCursor(response.nextCursor);
      setReviewed(true);
    } catch {
      if (!current()) return;
      owner.review = null;
      setCards([]);
      setError(scopeMismatch ? 'Physical cards unavailable. Your workspace changed. Reload this page to review its cards.'
        : 'Physical cards unavailable. Refresh your cards to review current information before taking another action.');
    } finally {
      if (current()) {
        owner.read = null;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    const owner = authority.current;
    owner.mounted = true;
    setViewOwner(ownerKey);
    setCards([]); setCapability(null); setNextCursor(null); setReviewed(false);
    setBusyCard(null); setSuccess(null); setError(null);
    void loadCards();
    return () => {
      owner.mounted = false;
      owner.generation++;
      owner.read?.abort();
      owner.read = null; owner.review = null; owner.scope = null; owner.command = null;
    };
  }, [ownerKey, loadCards]);

  const canAct = (card: CustomerPhysicalCard) => {
    const owner = authority.current;
    return owner.mounted && owner.owner === ownerKey && !owner.command && !owner.read &&
      Boolean(owner.review?.cards.includes(card));
  };
  const beginCommand = (card: CustomerPhysicalCard): Command | null => {
    if (!canAct(card)) return null;
    const owner = authority.current;
    const command = { id: Symbol('physical-card-command'), owner: ownerKey };
    owner.command = command;
    owner.review = null;
    setReviewed(false); setNextCursor(null); setCapability(null);
    setBusyCard(card.publicId); setError(null); setSuccess(null);
    return command;
  };
  const currentCommand = (command: Command) => {
    const owner = authority.current;
    return owner.mounted && owner.owner === command.owner && owner.command === command;
  };
  const runCommand = async (card: CustomerPhysicalCard, send: () => Promise<unknown>, message: (value: unknown) => string,
    navigate = false) => {
    const command = beginCommand(card);
    if (!command) return;
    try {
      // Sent commands are not aborted: cancellation cannot establish whether a mutation applied.
      const response = await send();
      if (!currentCommand(command)) return;
      const result = message(response);
      if (navigate) { window.location.assign(result); return; }
      setSuccess(result);
      await loadCards(null, command);
    } catch {
      if (currentCommand(command)) {
        setCards([]);
        setError(navigate
          ? 'Unable to confirm shipping checkout. Refresh your cards before trying again.'
          : 'The result of this change could not be confirmed. Refresh your cards and review the current state before trying again.');
      }
    } finally {
      if (currentCommand(command)) {
        authority.current.command = null;
        setBusyCard(null);
      }
    }
  };

  const changeCardState = async (card: CustomerPhysicalCard, targetState: PhysicalCardState) => {
    if (!canAct(card) || !authority.current.review?.capability.enabled || !card.lifecycle.allowedTargets.includes(targetState)) return;
    if (requiresPermanentConfirmation(targetState)) {
      const action = targetState === 'DESTROYED' ? 'mark this card as destroyed' : 'permanently retire this card';
      if (!window.confirm(`Are you sure you want to ${action}? This card will stop opening your Tap Page and cannot be reactivated.`)) return;
    }
    await runCommand(card, () => request(`/me/cards/${encodeURIComponent(card.publicId)}/lifecycle`, {
      method: 'POST', body: JSON.stringify({ requestId: crypto.randomUUID(), targetState,
        reason: targetState === 'LOST' ? 'Customer reported the physical card lost.' : `Customer requested ${targetState.toLowerCase()} state.` })
    }), (response) => {
      const parsed = customerCardLifecycleResponseSchema.safeParse(response);
      if (!parsed.success || parsed.data.card.publicId !== card.publicId || parsed.data.card.displayId !== card.displayId ||
          parsed.data.transition.toState !== targetState || (!parsed.data.transition.replayed && parsed.data.card.state !== targetState)) {
        throw new Error('Unconfirmed lifecycle result.');
      }
      return parsed.data.transition.replayed
        ? `${card.displayId}: the earlier change to ${STATE_COPY[targetState].toLowerCase()} was confirmed. Review the current card state below.`
        : `${card.displayId} is now ${STATE_COPY[targetState].toLowerCase()}.`;
    });
  };

  const nameCard = async (card: CustomerPhysicalCard) => {
    if (!canAct(card)) return;
    const nextName = window.prompt('Give this card a name, or leave it blank to remove its name.', card.friendlyName ?? '');
    if (nextName === null || !canAct(card)) return;
    const normalizedName = nextName.trim();
    if (normalizedName.length > 80) { setError('Card names can be up to 80 characters.'); return; }
    await runCommand(card, () => request(`/me/cards/${encodeURIComponent(card.publicId)}`, {
      method: 'PATCH', body: JSON.stringify({ friendlyName: normalizedName || null })
    }), (response) => {
      const parsed = customerCardNameResponseSchema.safeParse(response);
      if (!parsed.success || parsed.data.friendlyName !== (normalizedName || null)) throw new Error('Unconfirmed card name.');
      return normalizedName ? `Card named “${normalizedName}”.` : 'Card name removed.';
    });
  };

  const requestReplacement = async (event: FormEvent<HTMLFormElement>, card: CustomerPhysicalCard) => {
    event.preventDefault();
    if (!canAct(card)) return;
    const form = new FormData(event.currentTarget);
    const type = String(form.get('type') ?? 'DAMAGED');
    await runCommand(card, () => request(`/me/cards/${encodeURIComponent(card.publicId)}/replacement-requests`, {
      method: 'POST', body: JSON.stringify({ requestId: crypto.randomUUID(), type,
        explanation: String(form.get('explanation') ?? '').trim(), attestation: form.get('attestation') === 'on',
        lostCardConfirmation: form.get('lostCardConfirmation') === 'on' })
    }), (response) => {
      const parsed = customerCardReplacementResponseSchema.safeParse(response);
      if (!parsed.success || parsed.data.request.type !== type || parsed.data.card.displayId !== card.displayId ||
          parsed.data.card.disabledByReport !== (type === 'LOST' && parsed.data.card.state === 'LOST')) {
        throw new Error('Unconfirmed replacement result.');
      }
      return parsed.data.card.disabledByReport ? 'Replacement request received. The lost card has been disabled immediately.'
        : 'Replacement request received for Admin review.';
    });
  };

  const continueReplacement = async (card: CustomerPhysicalCard) => {
    if (!canAct(card)) return;
    const order = card.replacementRequest?.replacementOrder;
    if (!order || order.status !== 'PENDING' || order.inventoryReservation?.state !== 'ACTIVE') return;
    const returnUrl = `${window.location.origin}/card-design`;
    const body = cardOrderCheckoutRequestSchema.safeParse({ orderId: order.id, successUrl: returnUrl, cancelUrl: returnUrl });
    if (!body.success) { setError('Unable to open shipping confirmation.'); return; }
    await runCommand(card, () => request<unknown>('/billing/card-order-session', { method: 'POST', body: JSON.stringify(body.data) }),
      (response) => {
        const parsed = cardCheckoutSessionResponseSchema.safeParse(response);
        if (!parsed.success || !parsed.data.url) throw new Error('Unconfirmed shipping checkout.');
        return parsed.data.url;
      }, true);
  };
  const currentOwner = viewOwner === ownerKey;
  const actionsDisabled = !currentOwner || !reviewed || busyCard !== null || loading || loadingMore;
  const visibleCards = currentOwner ? cards : [];

  return (
    <section className="physical-card-controls" aria-labelledby="physical-card-controls-title">
      <header className="physical-card-controls__header">
        <div>
          <span>Physical cards</span>
          <h2 id="physical-card-controls-title" tabIndex={-1}>Lost Card Protection</h2>
          <p>Control each physical card without changing or unpublishing your Tap Page.</p>
        </div>
        {currentOwner && capability && !capability.enabled ? <Link to="/plan-billing">Compare Secure plans</Link> : null}
      </header>

      <button type="button" className="button-secondary" disabled={busyCard !== null} onClick={() => void loadCards()}>Refresh cards</button>
      {currentOwner && error ? <div className="alert" role="alert">{error}</div> : null}
      {currentOwner && success ? <div className="physical-card-controls__success" role="status">{success}</div> : null}

      {!currentOwner || loading ? (
        <p className="helper-text">Loading your physical cards…</p>
      ) : !reviewed && cards.length === 0 ? (
        <p className="helper-text">Physical cards unavailable.</p>
      ) : visibleCards.length === 0 ? (
        <div className="physical-card-controls__empty">
          <strong>No physical cards yet.</strong>
          <p>Your independently controllable cards will appear here after a paid order is provisioned.</p>
        </div>
      ) : (
        <div className="physical-card-list">
          {visibleCards.map((card) => (
            <article className="physical-card-item" key={card.publicId} data-state={card.state.toLowerCase()}>
              <div className="physical-card-item__identity">
                <div className="physical-card-item__mark" aria-hidden="true"><span>inktap</span></div>
                <div>
                  <span>{card.displayId}</span>
                  <h3>{card.friendlyName ?? card.product.name}</h3>
                  <p>{card.product.material} · {card.product.finish} · Unit {card.product.unit}</p>
                </div>
              </div>

              <div className="physical-card-item__status">
                <span className={`physical-card-state physical-card-state--${card.state.toLowerCase()}`}>{formatStatus(card.state)}</span>
                <strong>{STATE_COPY[card.state]}</strong>
                <p>{card.assignedTapPage?.label ?? 'No Tap Page assigned'}</p>
              </div>

              <dl className="physical-card-item__facts">
                <div><dt>NFC</dt><dd>{readinessLabel(card.readiness.nfc)}</dd></div>
                <div><dt>QR</dt><dd>{readinessLabel(card.readiness.qr)}</dd></div>
                <div><dt>Last used</dt><dd>{card.lastActivity ? `${formatDate(card.lastActivity.occurredAt)} · ${card.lastActivity.source}` : 'Not yet'}</dd></div>
              </dl>

              {capability?.enabled && card.lifecycle.allowedTargets.length > 0 ? (
                <div className="physical-card-item__actions">
                  <button type="button" className="button-secondary" disabled={actionsDisabled} onClick={() => void nameCard(card)}>
                    {card.friendlyName ? 'Rename card' : 'Name card'}
                  </button>
                  {card.lifecycle.allowedTargets.map((target) => (
                    <button
                      type="button"
                      key={target}
                      className={target === 'LOST' || requiresPermanentConfirmation(target) ? 'button-danger-subtle' : 'button-secondary'}
                      disabled={actionsDisabled}
                      onClick={() => void changeCardState(card, target)}
                    >
                      {busyCard === card.publicId ? 'Updating…' : ACTION_COPY[target] ?? formatStatus(target)}
                    </button>
                  ))}
                </div>
              ) : capability && !capability.enabled ? (
                <div className="physical-card-item__actions">
                  <button type="button" className="button-secondary" disabled={actionsDisabled} onClick={() => void nameCard(card)}>
                    {card.friendlyName ? 'Rename card' : 'Name card'}
                  </button>
                  <p className="physical-card-item__upgrade">Secure and Premium include instant lost-card controls.</p>
                </div>
              ) : (
                <div className="physical-card-item__actions">
                  <button type="button" className="button-secondary" disabled={actionsDisabled} onClick={() => void nameCard(card)}>
                    {card.friendlyName ? 'Rename card' : 'Name card'}
                  </button>
                </div>
              )}

              {card.replacementRequest ? (
                <div className="physical-card-item__replacement-status" role="status">
                  <strong>{OPEN_REPLACEMENT_STATUSES.has(card.replacementRequest.status) ? 'Replacement request' : 'Previous replacement request'}: {formatStatus(card.replacementRequest.status)}</strong>
                  <p>Submitted {formatDate(card.replacementRequest.requestedAt)}.</p>
                  {card.replacementRequest.fulfillmentMode ? <p>{card.replacementRequest.fulfillmentMode === 'WARRANTY_REPLACEMENT' ? 'Approved as a warranty replacement.' : 'Approved as a paid replacement.'}</p> : null}
                  {card.replacementRequest.resolution ? <p>{card.replacementRequest.resolution}</p> : null}
                  {card.replacementRequest.status === 'CANCELED' && card.replacementRequest.replacementCanceledAt ? (
                    <p>Canceled {formatDate(card.replacementRequest.replacementCanceledAt)}. Your original card was not changed.</p>
                  ) : null}
                  {card.replacementRequest.status === 'APPROVED' && !card.replacementRequest.replacementOrder ? <p>InkTap is preparing your replacement.</p> : null}
                  {card.replacementRequest.replacementOrder?.status === 'PENDING' && card.replacementRequest.replacementOrder.inventoryReservation?.state === 'ACTIVE' ? (
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={actionsDisabled}
                      onClick={() => void continueReplacement(card)}
                    >
                      {busyCard === card.publicId
                        ? 'Opening…'
                        : card.replacementRequest.replacementOrder.priceCents === 0
                          ? 'Confirm shipping — no charge'
                          : `Confirm shipping & pay ${formatMoney(card.replacementRequest.replacementOrder.priceCents, card.replacementRequest.replacementOrder.currency)}`}
                    </button>
                  ) : null}
                  {card.replacementRequest.replacementOrder?.status === 'PROCESSING' ? <p>Your replacement is in production.</p> : null}
                  {card.replacementRequest.replacementOrder?.status === 'SHIPPED' ? <p>Your replacement has shipped and is now the active card.</p> : null}
                  {card.replacementRequest.replacementOrder?.status === 'PENDING' && card.replacementRequest.replacementOrder.inventoryReservation?.state !== 'ACTIVE' ? <p>The shipping confirmation expired. InkTap will prepare a new one.</p> : null}
                </div>
              ) : null}

              {(!card.replacementRequest || !OPEN_REPLACEMENT_STATUSES.has(card.replacementRequest.status)) && ['ACTIVE', 'SUSPENDED', 'LOST'].includes(card.state) ? (
                <details className="physical-card-item__replacement-request">
                  <summary>Request a replacement</summary>
                  <form onSubmit={(event) => void requestReplacement(event, card)}>
                    <label>
                      What happened?
                      <select name="type" defaultValue="DAMAGED" disabled={actionsDisabled}>
                        <option value="DAMAGED">Damaged card</option>
                        <option value="MISPRINT">Print or manufacturing defect</option>
                        <option value="LOST">Lost card</option>
                        <option value="PAID_REPLACEMENT">Another reason</option>
                      </select>
                    </label>
                    <label>
                      Tell us what happened
                      <textarea name="explanation" minLength={20} maxLength={1000} required disabled={actionsDisabled} />
                    </label>
                    <label className="physical-card-item__confirmation">
                      <input name="lostCardConfirmation" type="checkbox" disabled={actionsDisabled} />
                      If I selected “Lost card,” I confirm that I no longer have this card.{capability?.enabled ? ' It will be disabled immediately.' : ' An Admin will review the report.'}
                    </label>
                    <label className="physical-card-item__confirmation">
                      <input name="attestation" type="checkbox" required disabled={actionsDisabled} />
                      I confirm this request is accurate and understand InkTap will review whether the replacement is warranty-covered or paid.
                    </label>
                    <button type="submit" className="button-secondary" disabled={actionsDisabled}>
                      {busyCard === card.publicId ? 'Submitting…' : 'Submit replacement request'}
                    </button>
                  </form>
                </details>
              ) : null}

              {(card.replacement.replaces || card.replacement.replacedBy || card.lifecycle.history.length > 0) ? (
                <details className="physical-card-item__history">
                  <summary>Lifecycle history</summary>
                  {card.replacement.replaces ? <p>Replaces {card.replacement.replaces}</p> : null}
                  {card.replacement.replacedBy ? <p>Replaced by {card.replacement.replacedBy}</p> : null}
                  <ol>
                    {card.lifecycle.history.map((entry, index) => (
                      <li key={`${entry.occurredAt}-${index}`}>
                        <strong>{formatStatus(entry.toState)}</strong>
                        <span>{formatDate(entry.occurredAt)} · {formatStatus(entry.actorType)}</span>
                        {entry.reason ? <p>{entry.reason}</p> : null}
                      </li>
                    ))}
                  </ol>
                </details>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {currentOwner && nextCursor ? (
        <button type="button" className="button-secondary" disabled={actionsDisabled} onClick={() => void loadCards(nextCursor)}>
          {loadingMore ? 'Loading…' : 'Load more cards'}
        </button>
      ) : null}
    </section>
  );
}

export default PhysicalCardControls;
